import { useCallback, useEffect } from 'react';

import { omit } from '@revenuemarket/utils';
import { useRouter } from 'next/router';

import { usePersistState } from '../usePersistState';
import { useQueryParam } from '../useQueryParam';

type NumericString = `${number}`;
type NumericStringToNumber<T> = T extends `${infer U extends number}` ? U : never;

export type StepModel<T extends readonly [unknown | undefined, ...unknown[]]> = {
  [K in Extract<keyof T, NumericString>]: {
    step: NumericStringToNumber<K>;
    model: T[K];
  };
}[Extract<keyof T, NumericString>];

const defaultStepModel = {
  step: 0,
  model: undefined,
};

interface Props<T extends readonly [unknown | undefined, ...unknown[]]> {
  name: string;
  defaultValue?: StepModel<T>;
}

export function useSteps<T extends readonly [unknown | undefined, ...unknown[]]>({
  defaultValue,
  name,
}: Props<T>) {
  const currentStep = useQueryParam('step', { parser: Number }) ?? 0;
  const router = useRouter();
  const [step, setStep] = usePersistState<StepModel<T>>({
    name,
    defaultValue: {
      ...(defaultValue ?? defaultStepModel),
      step: currentStep,
    } as StepModel<T>,
  });

  const moveStep = useCallback(
    (value: StepModel<T>) => {
      setStep(value);
      router.replace({ query: { ...router.query, step: value.step } });
    },
    [router, setStep],
  );

  const resetStep = useCallback(async () => {
    setStep((defaultValue ?? defaultStepModel) as StepModel<T>);
    await router.replace({ query: omit(router.query, ['step']) });
  }, [defaultValue, router, setStep]);

  useEffect(() => {
    return () => {
      resetStep();
    };
  }, []);

  return [step, moveStep, resetStep] as const;
}
